// Shared field catalogue. Single source for codes, labels and rule references used by
// the database (private.catalogue, checked in tests), the screens, the print/archive
// renderer and the paper fallback forms. Erasable TypeScript only (no enums), so
// Node can import it directly from scripts/build-docs.ts.
//
// Rule IDs point into research/codex-ptw-handoff/rules-register.json. They are
// source references for review, not adopted BadrEOR policy.

export type Option<C extends string = string> = {
  code: C
  label: string
  help?: string
  rule_ids?: readonly string[]
}

export const ROLES = {
  PA: { label: 'Permit Applicant', short: 'PA', rule_ids: ['CORE-026'] },
  RS: { label: 'Responsible Supervisor', short: 'RS', rule_ids: ['CORE-049', 'CORE-052'] },
  // OM: a BadrEOR role with no SP-2329 source rule (0016) - the independent approval, once per
  // authorised permit, before any work starts.
  OM: { label: 'Operations Manager', short: 'OM', rule_ids: [] },
  /** BadrEOR has no Area Authority (0016): the RS holds every AA duty. Kept only to label
   *  signatures made before 0016 and sandboxes still on the older policy versions. */
  AA: { label: 'Area Authority (historic)', short: 'AA', rule_ids: ['CORE-055', 'CORE-036'] },
  PH: { label: 'Permit Holder', short: 'PH', rule_ids: ['CORE-057', 'CORE-058'] },
  AGT: { label: 'Authorised Gas Tester', short: 'AGT', rule_ids: ['CORE-036', 'CORE-041'] },
  OAC: { label: 'Other Affected Custodian', short: 'OAC', rule_ids: ['CORE-047'] },
  PC: { label: 'Production Coordinator', short: 'PC', rule_ids: ['CORE-048'] },
  IA: { label: 'Isolating Authority', short: 'IA', rule_ids: ['ACT-001'] },
  CREW: { label: 'Work-party member', short: 'Crew', rule_ids: ['CORE-060'] },
} as const
export type RoleCode = keyof typeof ROLES

/**
 * Roles the licence register records (0021) and the credential kind each is kept as:
 * PTW licences (PA, RS, PH), the gas-tester certificate (AGT) and appointments (OM, PC).
 * Must match private.licence_role_kind (checked by tests/db/licences.test.ts).
 */
export const LICENCE_ROLES: readonly { code: RoleCode; kind: 'ptw_licence' | 'agt_certificate' | 'appointment' }[] = [
  { code: 'PA', kind: 'ptw_licence' },
  { code: 'RS', kind: 'ptw_licence' },
  { code: 'PH', kind: 'ptw_licence' },
  { code: 'AGT', kind: 'agt_certificate' },
  { code: 'OM', kind: 'appointment' },
  { code: 'PC', kind: 'appointment' },
]

export const ACTIVITY_GROUPS = [
  'Well Testing',
  'OTSG',
  'Water Treatment',
  'Chemical Injection',
  'Hot Water Pumping',
  'Workshop',
  'Surface Preparation',
  'Pressure Testing',
] as const
export type ActivityGroup = (typeof ACTIVITY_GROUPS)[number]

/**
 * The task activity group that asks for the pressure-test details (type and actual medium)
 * and a verified procedure (ACT-070). Like Surface Preparation it decides which questions
 * are asked; it never sets the permit class, a medium or any limit.
 */
export const PRESSURE_TEST_GROUP: ActivityGroup = 'Pressure Testing'

/**
 * Work-location classification chosen by the applicant (0015; CORE-031). `label` is the
 * text sealed with the revision (private.app_location_label); `option` is what the
 * drop-down shows inside its `group`. The server maps it for the examination schedule
 * (private.app_location_flags): process_facility -> process facility and hydrocarbon area;
 * hydrocarbon_area -> hydrocarbon area; critical_sour -> hydrocarbon area and sour.
 */
export const LOCATION_CLASSIFICATIONS = [
  { code: 'process_facility', label: 'Process facilities', option: 'Process facilities', group: null },
  { code: 'hydrocarbon_area', label: 'Hydrocarbon areas', option: 'Hydrocarbon areas', group: null },
  { code: 'critical_sour', label: 'Critical sour', option: 'Critical sour', group: null },
  { code: 'nha_workshop', label: 'Non-hydrocarbon areas - Workshop', option: 'Workshop', group: 'Non-hydrocarbon areas' },
  { code: 'nha_water_treatment_plant', label: 'Non-hydrocarbon areas - Water Treatment Plant', option: 'Water Treatment Plant', group: 'Non-hydrocarbon areas' },
  { code: 'nha_restricted_area', label: 'Non-hydrocarbon areas - Restricted areas', option: 'Restricted areas', group: 'Non-hydrocarbon areas' },
  {
    code: 'nha_other_badreor_controlled',
    label: 'Non-hydrocarbon areas - Work in other areas under BadrEOR control',
    option: 'Work in other areas under BadrEOR control',
    group: 'Non-hydrocarbon areas',
  },
] as const
export type LocationClassificationCode = (typeof LOCATION_CLASSIFICATIONS)[number]['code']

/**
 * Activities by permit class from PDO SP-2329 Rev 2.0 Appendix 1, "PTW type and controls
 * for activities in each type of work area" (pages 85-91). One entry per activity number:
 * the class is the colour of that cell (red = Class A, blue = Class B) for the location
 * shown; cells marked No Permit Job, N/A, "No PTW required" or "use another form" are not
 * listed. The applicant lists the activities of the class they chose (0015); an activity
 * never sets the permit class or any limit. The same list is in SQL
 * (private.app_class_activities, 0015), checked by tests/db/foundation.test.ts.
 */
export type ClassActivity = {
  code: string
  /** SP-2329 Appendix 1 activity number. */
  item: string
  permit_class: 'A' | 'B'
  group: string
  activity: string
  location: string
  page: number
  /** What is shown and sealed: "<item> <activity> - <location>". */
  label: string
}

const PF = 'Process facility'
const HCA = 'Hydrocarbon area'
const PF_HCA = 'Process facility and hydrocarbon area'
const HAZ = 'Hazardous area (zone 1 or 2)'
const NHAZ = 'Non-hazardous area'
const HAZ_NHAZ = 'Hazardous and non-hazardous areas'

const ACTIVITY_GROUP_TITLES: Record<string, string> = {
  '1': 'Hot work',
  '2': 'Confined space work',
  '3': 'Electrical work and work in electrical areas',
  '4': 'Handling hazardous substances',
  '5': 'Work on safety systems',
  '6': 'Mechanical maintenance activities',
  '7': 'Work at height',
  '8': 'Scaffolding activities',
  '9': 'Operations using cranes and other heavy machinery',
  '10': 'Systems and equipment testing and commissioning / decommissioning',
  '11': 'Use of non-certified equipment in hazardous areas',
  '12': 'Civil works',
  '13': 'Operational activities',
  '14': 'Faultfinding',
  '15': 'ESP activities',
  '16': 'Electrical installations',
}

const RAW_CLASS_ACTIVITIES: ReadonlyArray<readonly [string, 'A' | 'B', string, string, number]> = [
  ['1.1', 'A', 'Welding (gas) / oxy-acetylene cutting (hot work)', PF, 85],
  ['1.2', 'B', 'Welding (gas) / oxy-acetylene cutting (hot work)', HCA, 85],
  ['1.3', 'A', 'Welding (arc) (hot work)', PF, 85],
  ['1.4', 'B', 'Welding (arc) (hot work)', HCA, 85],
  ['1.5', 'A', 'Grinding (hot work)', PF, 85],
  ['1.6', 'B', 'Grinding (hot work)', HCA, 85],
  ['2.1', 'A', 'Confined space entry, except well cellars and Casasco pits', PF_HCA, 85],
  ['2.2', 'B', 'Entry into well cellars and Casasco pits, and excavations below 1.5 m', PF_HCA, 85],
  ['3.1', 'B', 'Work requiring an electrical safety document under the Electrical Safety Rules', PF_HCA, 85],
  ['3.2', 'B', 'Opening live electrical junction boxes by Electrical Authorised Persons', PF_HCA, 85],
  ['4.1', 'B', 'Handling substances hazardous to health, e.g. toxic chemicals or asbestos', PF_HCA, 86],
  ['4.2', 'B', 'Use of radioactive sources', PF_HCA, 86],
  ['4.3', 'B', 'Contamination with naturally occurring radioactive material (NORM)', PF_HCA, 86],
  ['4.4', 'A', 'Working with pyrophoric scale (except scale met during pigging)', PF, 86],
  ['4.5', 'B', 'Working with pyrophoric scale (except scale met during pigging)', HCA, 86],
  ['5.1', 'A', 'Work that affects the availability of ESD and F&G systems', PF, 86],
  ['5.2', 'B', 'Work that affects the availability of process and machinery control systems', PF, 86],
  ['6.1', 'A', 'Cold cutting pipe on systems containing hydrocarbons or hazardous fluids', PF_HCA, 86],
  ['6.2', 'B', 'Unbolting flanges on systems containing hydrocarbons or hazardous fluids', PF_HCA, 86],
  ['6.3', 'B', 'Grit blasting (hot work)', PF_HCA, 86],
  ['6.4', 'B', 'Power or hand tools (except grinders) that may create a spark between surface and tool', PF_HCA, 86],
  ['6.5', 'B', 'High-pressure (HP) water jetting', PF_HCA, 86],
  ['6.6', 'B', 'Painting (hand and spray)', PF_HCA, 86],
  ['6.7', 'B', 'Engineering and routine/corrective maintenance that does not unbolt flanges or cut into systems that contained hydrocarbons or hazardous fluids', PF_HCA, 86],
  ['6.8', 'B', 'Leak clamping of oil/gas flowlines', PF_HCA, 86],
  ['6.9', 'B', 'Beam pump/PCP maintenance', PF_HCA, 86],
  ['6.10', 'A', 'Polyethylene lining of pipelines and flowlines', PF, 86],
  ['6.11', 'B', 'Polyethylene lining of pipelines and flowlines', HCA, 86],
  ['6.12', 'B', 'Vacuum tanker discharge inside a process area', PF, 87],
  ['6.13', 'B', 'Suction tanker operations inside a process area', PF, 87],
  ['6.14', 'B', 'Engineering and routine/corrective maintenance that uses lifting equipment', PF_HCA, 87],
  ['7.1', 'B', 'Work where a person may fall more than 2 m, except on approved walkways and scaffolds', PF_HCA, 87],
  ['8.1', 'B', 'Erection or dismantling of scaffolding', PF_HCA, 87],
  ['9.1', 'A', 'Heavy machinery (cranes, excavators, trucks) that could pass over or touch live hydrocarbon, HP steam or chemical systems or overhead power lines', PF, 87],
  ['9.2', 'B', 'Heavy machinery (cranes, excavators, trucks) that could pass over or touch live hydrocarbon, HP steam or chemical systems or overhead power lines', HCA, 87],
  ['10.1', 'B', 'Pressure testing of plant and equipment', PF_HCA, 87],
  ['10.2', 'B', 'Commissioning systems and equipment', PF_HCA, 87],
  ['10.3', 'B', 'Decommissioning', PF_HCA, 87],
  ['11.1', 'B', 'Temporary diesel engines in hazardous areas, including vehicle access, mobile cranes and refuelling', PF_HCA, 87],
  ['11.2', 'B', 'Use of non-intrinsically safe equipment (including non-IS GPS in a fenced process area)', PF_HCA, 87],
  ['11.3', 'B', 'Use of intrinsically safe test or portable equipment', PF, 87],
  ['12.1', 'B', 'Excavation (hand excavation to 300 mm where pipes or cables may be present)', PF_HCA, 88],
  ['12.2', 'B', 'Rebar cutting, bending and shuttering (form work) and concrete pouring', PF_HCA, 88],
  ['12.3', 'B', 'Engineering construction not involving welding, burning or grinding', PF_HCA, 88],
  ['12.4', 'B', 'Roadway levelling and grading using machinery', PF_HCA, 88],
  ['13.1', 'B', 'Well Services operations where well-site custody has not been transferred to Well Services', PF_HCA, 88],
  ['13.2', 'B', 'Drilling Water Services activities listed in procedure DWSOP P033', PF_HCA, 88],
  ['13.4', 'B', 'Seismic exploration activities', PF_HCA, 88],
  ['13.5', 'B', 'Geomatic survey', PF, 89],
  ['13.6', 'A', 'Sampling process fluids classed as low, high or very high risk sour service, or not yet classified', PF, 89],
  ['13.8', 'B', 'Sampling process fluids classed as sweet', PF, 89],
  ['13.9', 'B', 'Use of a flushby unit following dedicated contractor procedures', PF, 89],
  ['14.1', 'B', 'Minor faultfinding work (controlled isolation of valves, removing instruments for calibration, similar minor tasks)', PF, 89],
  ['15.2', 'B', 'ESP fluid shot', HAZ, 90],
  ['15.3', 'B', 'Commissioning ESP, ESPCP and PCP', HAZ_NHAZ, 90],
  ['15.4', 'B', 'ESP powered by a generator', HAZ, 90],
  ['16.1', 'A', 'Work at electrical installations requiring an electrical safety document under the Electrical Safety Rules', HAZ, 91],
  ['16.2', 'B', 'Construction at electrical installations (excavation, civil and electrical work, crane lifting, shot blasting, painting) by a third party or under PDO supervision', HAZ_NHAZ, 91],
  ['16.3', 'B', 'Third-party periodic visits to electrical substations and power stations for routine electrical work', NHAZ, 91],
  ['16.4', 'B', 'Live line washing on overhead lines to process field facilities', HAZ, 91],
  ['16.5', 'B', 'Opening live electrical junction boxes by Electrical Authorised Persons (hot work)', HAZ, 91],
  ['16.6', 'B', 'Low voltage (LV) work requiring an electrical safety document', HAZ, 91],
  ['16.7', 'B', 'Non-electrical work in electrical switch rooms, power stations or near overhead lines', HAZ, 91],
  ['16.8', 'B', 'Electrical switching controlled by ESOPs and ESRs', HAZ_NHAZ, 91],
  ['16.9', 'A', 'Electrical maintenance at unmanned substations and power stations by Power Systems Operations staff', HAZ, 91],
  ['16.10', 'B', 'Non-routine electrical work in power stations and substations by Power Systems Operations staff', HAZ_NHAZ, 91],
  ['16.11', 'B', 'Non-electrical work in power stations and substations by Power Systems Operations staff', HAZ, 91],
  ['16.12', 'B', 'Non-electrical work in power stations and substations by Power Systems Operations staff', NHAZ, 91],
]

export const CLASS_ACTIVITIES: readonly ClassActivity[] = RAW_CLASS_ACTIVITIES.map(([item, permit_class, activity, location, page]) => ({
  code: `app1_${item.replace('.', '_')}`,
  item,
  permit_class,
  group: `${item.split('.')[0]}. ${ACTIVITY_GROUP_TITLES[item.split('.')[0]]}`,
  activity,
  location,
  page,
  label: `${item} ${activity} - ${location}`,
}))

/** The listed activities of one permit class. */
export function classActivitiesFor(permitClass: string | null | undefined): readonly ClassActivity[] {
  return CLASS_ACTIVITIES.filter((a) => a.permit_class === permitClass)
}

/** Kept as three separate labels; a label never sets a class, medium or limit (ACT-069). */
export const PRESSURE_TEST_TYPES = ['Hydrostatic', 'Pneumatic', 'Hydraulic'] as const
export type PressureTestType = (typeof PRESSURE_TEST_TYPES)[number]

export const PERMIT_CLASSES: readonly Option<'B' | 'A' | 'TASK_SOP' | 'NPJ'>[] = [
  { code: 'B', label: 'Class B (blue) - medium risk', help: 'Built in this demonstration: up to 28 days authorisation, daily validation.', rule_ids: ['CORE-007', 'CORE-009'] },
  { code: 'A', label: 'Class A (red) - high risk', help: 'High-risk work. Production Coordinator approval before authorisation, a worksite examination at every validation in a process facility, and the validating RS stays in the area. Only where the rules in use enable Class A.', rule_ids: ['CORE-007', 'CORE-008'] },
  { code: 'TASK_SOP', label: 'Task SOP / Notification of Work', help: 'A distinct PH-controlled workflow with its own risk assessment. Not built yet.', rule_ids: ['CORE-013', 'CORE-014'] },
  { code: 'NPJ', label: 'No Permit Job', help: 'A separate RS-controlled form, still with controls. Not built yet.', rule_ids: ['CORE-015'] },
]

export const PREISSUE_CHECKS: readonly Option[] = [
  { code: 'frozen_plan_or_moc', label: 'Job is in the current frozen plan or has a valid MoC entry' },
  { code: 'no_conflicts', label: 'No conflicts with other work at this location' },
  { code: 'simops_controls_understood', label: 'Required SIMOPS controls are identified and understood' },
  { code: 'affected_custodians_informed', label: 'Affected custodians have been informed' },
].map((c) => ({ ...c, rule_ids: ['CORE-052'] }))

/**
 * The sixteen subjects the RS checks before every validation (CORE-055 gives them to the Area
 * Authority; in BadrEOR the RS holds that duty). Codes keep their historic aa_ prefix.
 */
export const AA_VALIDATION_CHECKS: readonly Option[] = [
  { code: 'plan_or_moc', label: 'Job is in the frozen plan or has a valid MoC' },
  { code: 'ph_understands_scope', label: 'PH understands the scope, hazards and controls' },
  { code: 'gis_conflicts', label: 'No location conflicts on the plot plan / GIS' },
  { code: 'plot_plan_registers', label: 'Operational plot-plan registers checked' },
  { code: 'cumulative_dynamic_override_risk', label: 'Cumulative, dynamic and override risk considered' },
  { code: 'prerequisite_controls', label: 'Prerequisite controls are in place' },
  { code: 'aa_controls_understood', label: 'RS controls complied with and understood' },
  { code: 'gas_tests', label: 'Required gas tests complete, or pre-arranged before work starts', help: 'Pre-arranged is allowed here; work still cannot start without a completed acceptable test.' },
  { code: 'isolations_in_place', label: 'Required isolations are in place' },
  { code: 'checklists_complied', label: 'Required checklists complied with' },
  { code: 'vehicle_requirements', label: 'Vehicle requirements met' },
  { code: 'method_statements_understood', label: 'Method statements understood' },
  { code: 'breaking_containment_proof', label: 'PH understands the witnessed bleed proof for breaking containment' },
  { code: 'isolation_copy_in_rack', label: 'Isolation copy and tag stubs are in the rack' },
  { code: 'worksite_examination', label: 'Required worksite examination done' },
  { code: 'aa_attends_tric_high_risk', label: 'RS attends the TRIC for breaking containment or high-risk work' },
].map((c) => ({ ...c, rule_ids: ['CORE-055'] }))

export const MEETING_CHECKS: readonly Option[] = [
  { code: 'planning_status_verified', label: 'Frozen-plan or emergency status verified' },
  { code: 'notice_adequate_or_justified', label: 'Notice is adequate, or urgency is justified' },
  { code: 'permit_type_correct', label: 'Permit type is correct' },
  { code: 'scope_location_clear', label: 'Scope and location are clear' },
  { code: 'hazards_controls_adequate', label: 'Hazards and controls are adequate' },
  { code: 'simops_mopo_reviewed', label: 'SIMOPS and MOPO reviewed' },
  { code: 'gas_testing_plan_adequate', label: 'Gas testing plan is adequate' },
  { code: 'isolation_plan_agreed', label: 'Isolation plan agreed (or none needed)' },
  { code: 'supporting_documents_identified', label: 'Supporting documents identified' },
].map((c) => ({ ...c, rule_ids: ['CORE-045'] }))

/** Toolbox talk topics (CORE-060). */
export const TBT_TOPICS: readonly Option[] = [
  { code: 'work_plan_methods', label: 'The work, the plan and the methods' },
  { code: 'jhsep_preparations', label: 'Job HSE plan and preparations' },
  { code: 'responsibilities_skills', label: 'Responsibilities and skills' },
  { code: 'procedures', label: 'Procedures to follow' },
  { code: 'environment_hazards_controls', label: 'Environment, hazards and controls' },
  { code: 'equipment_materials', label: 'Equipment and materials' },
  { code: 'isolation_proving', label: 'Isolation proving (if any)' },
  { code: 'conflicts', label: 'Conflicts with nearby work' },
  { code: 'access_evacuation', label: 'Access, escape and evacuation' },
  { code: 'crew_questions', label: 'Crew questions answered' },
].map((c) => ({ ...c, rule_ids: ['CORE-060'] }))

/** What the PH declares when accepting a cycle (CORE-057/058). */
export const ACCEPTANCE_DECLARATIONS: readonly Option[] = [
  { code: 'concurrent_work_reviewed', label: 'I reviewed concurrent work nearby and paused/checked with affected crews' },
  { code: 'prerequisite_controls_verified', label: 'I verified the prerequisite controls are in place' },
  { code: 'jhsep_understood', label: 'I understand the Job HSE plan' },
  { code: 'crew_briefed_tric_dra', label: 'I briefed the crew with the TRIC and its dynamic risk plan' },
  { code: 'crew_signed_understanding', label: 'Every crew member signed their understanding' },
  { code: 'aa_validation_confirmed', label: 'The RS has validated this cycle' },
  { code: 'site_safe', label: 'I am satisfied the site is safe to start' },
  { code: 'sight_and_communication', label: 'I can keep the work in sight and communication' },
  { code: 'permit_displayed', label: 'The permit and JHSEP are displayed at the worksite' },
].map((c) => ({ ...c, rule_ids: ['CORE-057', 'CORE-058'] }))

export const SUSPENSION_REASONS: readonly Option[] = [
  { code: 'unsafe_condition', label: 'Unsafe condition developed or suspected' },
  { code: 'reportable_incident', label: 'Reportable incident occurred' },
  { code: 'nearby_leak', label: 'Gas or oil leak nearby' },
  { code: 'interruption_over_limit', label: 'Work interrupted for more than four hours' },
  { code: 'conflicting_priority_work', label: 'Higher-priority conflicting work' },
  { code: 'deisolation_for_test', label: 'Equipment needs de-isolation for test' },
  { code: 'unsafe_gas_result', label: 'Unsafe gas test result' },
  { code: 'end_of_validation_period', label: 'End of the validation period or shift' },
  { code: 'other', label: 'Other (describe)' },
].map((c) => ({ ...c, rule_ids: ['CORE-062'] }))

export const CANCELLATION_REASONS: readonly Option[] = [
  { code: 'work_complete', label: 'Work complete' },
  { code: 'authorisation_ended', label: 'Authorisation period ended' },
  { code: 'suspended_beyond_authorisation', label: 'Work will stay suspended beyond the authorisation' },
  { code: 'scope_differs', label: 'Work differs from the stated scope (new permit needed)' },
  { code: 'incomplete_new_permit_needed', label: 'Work incomplete; a new permit is needed' },
].map((c) => ({ ...c, rule_ids: ['CORE-070'] }))

export const PH_CANCELLATION_DECLARATIONS: readonly Option[] = [
  { code: 'site_equipment_secured', label: 'Site and equipment left secure' },
  { code: 'tools_equipment_removed_or_listed', label: 'Tools and equipment removed (or what remains is listed)' },
  { code: 'package_returned', label: 'Permit package returned for closeout and archive' },
].map((c) => ({ ...c, rule_ids: ['CORE-071'] }))

/** What the RS checks when closing out a cancelled permit (CORE-072; the AA in the source). */
export const AA_CLOSEOUT_CHECKS: readonly Option[] = [
  { code: 'worksite_inspected', label: 'Worksite inspected' },
  { code: 'safe_condition_confirmed', label: 'Safe condition confirmed' },
  { code: 'work_status_confirmed', label: 'Complete / incomplete status confirmed' },
  { code: 'operation_checked_when_required', label: 'Operation checked where required' },
  { code: 'isolation_disposition_accounted', label: 'Every isolation accounted for (de-isolated, transferred, or left under an EPI)', help: 'A permit that needed isolation records what became of it below (0031). A permit that needed none records "none linked".' },
].map((c) => ({ ...c, rule_ids: ['CORE-072'] }))

export const GAS_TRIGGERS: readonly Option[] = [
  { code: 'flammable_atmosphere_hot_work', label: 'Hot work where flammable gas may be present' },
  { code: 'process_facility_vehicle_access', label: 'Vehicle entry to a process facility' },
  { code: 'diesel_engine_equipment', label: 'Diesel-engine equipment' },
  { code: 'non_intrinsically_safe_equipment', label: 'Non-intrinsically-safe equipment' },
  { code: 'confined_space_entry', label: 'Vessel or confined-space entry' },
  { code: 'possible_toxic_atmosphere', label: 'Toxic gas or vapour may be present' },
].map((c) => ({ ...c, rule_ids: ['CORE-037'] }))

export const GAS_TEST_KINDS: readonly Option[] = [
  { code: 'pre_start', label: 'Before work starts' },
  { code: 'periodic', label: 'Repeat at the planned interval' },
  { code: 'restart', label: 'Before restart (site unattended over one hour)' },
  { code: 'post_stop_check', label: 'Check after a stop' },
].map((c) => ({ ...c, rule_ids: ['CORE-038'] }))

/** Species offered in the synthetic profile. Units are labels; no limits exist (DEC-CORE-005). */
export const GAS_SPECIES: readonly Option[] = [
  { code: 'O2', label: 'Oxygen' },
  { code: 'LEL', label: 'Flammable (LEL)' },
  { code: 'H2S', label: 'Hydrogen sulphide' },
  { code: 'CO', label: 'Carbon monoxide' },
]

/**
 * Kinds of isolation an application can declare (0031; SP-2329 11.4 "Isolation Type", ACT-002,
 * ACT-004). One or more per application. Must match private.catalogue('isolation_kind').
 */
export const ISOLATION_KINDS: readonly Option[] = [
  { code: 'process', label: 'Process isolation', help: 'Valves closed and locked, lines depressurised, drained, purged or vented.' },
  { code: 'mechanical', label: 'Mechanical isolation', help: 'Spades or blinds, spool removal, physical disconnection, moving parts immobilised.' },
  { code: 'electrical', label: 'Electrical isolation', help: 'HV, LV or ELV isolation under the Electrical Safety Rules. The approval authority (AEP/CEP) and key custody are open (DEC-ACT-001).' },
  { code: 'instrument', label: 'Instrument or control isolation', help: 'Instrument loops, trips or control signals isolated. A safeguarding override has its own supporting document.' },
].map((c) => ({ ...c, rule_ids: ['ACT-002', 'ACT-004'] }))

/**
 * What became of the isolation when the permit ended (0031; CORE-072, ACT-015, ACT-019). The RS
 * records one at closeout for a permit that needed isolation. Must match
 * private.catalogue('isolation_disposition').
 */
export const ISOLATION_DISPOSITIONS: readonly Option[] = [
  { code: 'de_isolated', label: 'De-isolated', help: 'The isolation was removed under the approved de-isolation plan on the Isolation Certificate.' },
  { code: 'transferred', label: 'Transferred to another permit', help: 'The isolation stays in place for a named permit (give its number).' },
  { code: 'extended_period_isolation', label: 'Left in place as an extended period isolation (EPI)', help: 'No work continues inside the boundary; the EPI register (F09) carries it and the RS reviews it every 90 days.' },
].map((c) => ({ ...c, rule_ids: ['CORE-072', 'ACT-015', 'ACT-019'] }))

/** Every disposition code the database can hold, including those the server sets itself. */
export const ISOLATION_DISPOSITION_LABELS: Record<string, string> = {
  none_linked: 'None - this permit needed no isolation',
  pending_closeout: 'In place - the RS records its disposition at closeout',
  de_isolated: 'De-isolated',
  transferred: 'Transferred to another permit',
  extended_period_isolation: 'Left in place as an extended period isolation (EPI)',
}

export const SUPPORTING_DOCUMENT_KINDS = [
  'Isolation certificate', 'Overhead-line clearance', 'Vehicle/TPE location', 'Sanction for test',
  'Limitation of access', 'Lifting plan', 'Safeguarding override', 'Drawings', 'ORA', 'PEFS/UEFS', 'SHOC',
  'Manual handling assessment', 'Health risk assessment', 'Excavation certificate', 'Electrical safety document',
  'Radiography', 'Confined-space entry', 'Vehicle access checklist', 'Method statement', 'Other',
] as const

export const PERMIT_STATES: Record<string, { label: string; tone: 'neutral' | 'info' | 'ok' | 'warn' | 'bad' }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  submitted: { label: 'Submitted for review', tone: 'info' },
  returned_for_revision: { label: 'Returned for revision', tone: 'warn' },
  reviewed: { label: 'Reviewed - awaiting authorisation', tone: 'info' },
  authorised: { label: 'Authorised', tone: 'ok' },
  cancellation_signed: { label: 'Cancelled by PH - awaiting RS closeout', tone: 'warn' },
  closed_out: { label: 'Closed out - archive pending', tone: 'neutral' },
  archived: { label: 'Archived', tone: 'neutral' },
}

export const CYCLE_STATES: Record<string, { label: string; tone: 'neutral' | 'info' | 'ok' | 'warn' | 'bad' }> = {
  requested: { label: 'Requested', tone: 'info' },
  preissued: { label: 'Pre-issued by RS', tone: 'info' },
  validated: { label: 'Validated by RS', tone: 'info' },
  accepted: { label: 'Accepted by PH', tone: 'ok' },
  suspension_reported: { label: 'Stop reported', tone: 'bad' },
  suspended: { label: 'Suspended', tone: 'warn' },
  ended: { label: 'Ended', tone: 'neutral' },
}

/** Plain words for event kinds whose code does not read well with the underscores removed. */
export const EVENT_KIND_LABELS: Record<string, string> = {
  permit_om_approved: 'Approved by the Operations Manager',
  permit_recalled: 'Recalled by the applicant to correct it',
  permit_extension_requested: 'Extension requested',
  permit_extension_approved: 'Extension approved by the RS',
  permit_extension_refused: 'Extension refused by the RS',
  permit_extension_withdrawn: 'Extension request withdrawn',
}

/** "Approved by the Operations Manager" for permit_om_approved, else the code in words. */
export function eventKindLabel(kind: string): string {
  return EVENT_KIND_LABELS[kind] ?? kind.replace(/_/g, ' ')
}

/**
 * Section numbering shared by the printed permit, the archive render and the paper
 * fallback form, so all three read the same way.
 */
export const PERMIT_SECTIONS = [
  { no: 1, code: 'application', title: 'Application and work description', signer: 'PA' },
  { no: 2, code: 'location', title: 'Location, classification and hazards', signer: 'PA' },
  { no: 3, code: 'tasks', title: 'Tasks, methods and procedures', signer: 'PA' },
  { no: 4, code: 'jhsep', title: 'Job HSE plan, controls, SIMOPS and MOPO', signer: 'PA' },
  { no: 5, code: 'gas_plan', title: 'Gas testing plan', signer: 'PA' },
  { no: 6, code: 'supporting', title: 'Supporting documents and certificates', signer: 'PA / RS' },
  { no: 7, code: 'review', title: 'Permit meeting review and other affected custodians', signer: 'RS / OAC' },
  { no: 8, code: 'authorisation', title: 'Authorisation and extensions', signer: 'RS / OM' },
  { no: 9, code: 'daily_cycle', title: 'Daily cycle: pre-issue, validation and acceptance', signer: 'RS / PH' },
  { no: 10, code: 'gas_record', title: 'Gas test record', signer: 'AGT' },
  { no: 11, code: 'tbt', title: 'Toolbox talk and TRIC attendance', signer: 'PH / Crew' },
  { no: 12, code: 'suspension', title: 'Suspension and revalidation', signer: 'PH / RS' },
  { no: 13, code: 'cancellation', title: 'Cancellation (PH) and closeout (RS)', signer: 'PH / RS' },
  { no: 14, code: 'record', title: 'Record control: events, signatures and archive', signer: 'System' },
] as const

/** Engineering note displayed wherever a synthetic record appears. */
export const SYNTHETIC_NOTICE =
  'SYNTHETIC DEMONSTRATION - fictitious people, sites and qualifications. Not a permit to work.'
